import React, { useRef, useEffect } from "react";
import { ChatMessage } from "../../../types/chat";
import { chatService } from "../../../services/ChatService";
import { formatChatTimestamp } from "@/utils/helpers";
import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import env from "@/utils/env";
import { SystemMessageDivider } from "./SystemMessageDivider";
import styles from "./MessageList.module.css";

interface MessageListProps {
  messages?: ChatMessage[];
  userId?: string;
  guestEmail?: string;
  isGuest?: boolean;
  chatRoom?: any;
  chatStatus?: 'pending' | 'active' | 'ended';
  onReply?: (message: ChatMessage) => void;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  userId,
  guestEmail,
  isGuest = false,
  onReply,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const getFileUrl = (message: ChatMessage) => {
    if (message.content.file?.url) {
      return message.content.file.url;
    }
    return `${env.storageUrl}/storage/${
      message.content.file?.path
    }`;
  };

  const getFileType = (message: ChatMessage) => {
    return message.content.file?.type || "";
  };

  return (
    <div className={styles.container}>
      {messages?.map((message) => {
            const isSystemMessage = message.content.sender_type === "system";
            const isCurrentUser = isGuest
              ? message.content.metadata.guest_email === guestEmail
              : message.sender_id != null && Number(message.sender_id) === Number(userId);

            if (isSystemMessage) {
              return (
                <SystemMessageDivider key={message.id} body={message.content.body} />
              );
            }

            return (
              <div
                key={message.id}
                className={`group ${styles.messageRow} ${
                  isCurrentUser ? styles.messageRowEnd : styles.messageRowStart
                }`}
              >
                <>
                  {isCurrentUser && onReply && (
                    <button
                      onClick={() => onReply(message)}
                      className={styles.replyBtn}
                      title="Reply"
                    >
                      <ArrowUturnLeftIcon className={styles.replyIcon} />
                    </button>
                  )}
                  <div
                    className={`${styles.messageBubble} ${
                      isCurrentUser ? styles.messageBubbleSelf : styles.messageBubbleOther
                    }`}
                  >
                    <div className={isCurrentUser ? styles.senderSelf : styles.senderOther}>
                      {isGuest
                        ? message.content.metadata.guest_name
                        : `${message.sender?.name} (${
                            message.content.sender_role || "User"
                          })`}
                    </div>

                    {message.content.metadata?.reply_to && (
                      <div
                        className={`${styles.replyQuote} ${
                          isCurrentUser ? styles.replyQuoteSelf : styles.replyQuoteOther
                        }`}
                      >
                        <span className={styles.replyQuoteSender}>
                          {message.content.metadata.reply_to.sender_name}
                        </span>
                        : {message.content.metadata.reply_to.body}
                      </div>
                    )}

                    {message.content.message_type === "file" ? (
                      <div className={styles.fileAttachment}>
                        {getFileType(message).startsWith("image/") ? (
                          <img
                            src={getFileUrl(message)}
                            alt={message.content.file?.name}
                            className={styles.filePreviewImg}
                            loading="lazy"
                            onClick={() =>
                              window.open(getFileUrl(message), "_blank")
                            }
                          />
                        ) : (
                          <>
                            <div className={styles.fileInfo}>
                              <span className={styles.fileIcon}>📎</span>
                              <span className={styles.fileName}>
                                {message.content.file?.name}
                              </span>
                            </div>
                            {getFileType(message).startsWith("video/") && (
                              <video
                                controls
                                className={styles.filePreviewVideo}
                                src={getFileUrl(message)}
                              />
                            )}
                            {getFileType(message).startsWith("audio/") && (
                              <audio
                                controls
                                className={styles.filePreviewAudio}
                                src={getFileUrl(message)}
                              />
                            )}
                            <button
                              onClick={async () => {
                                const blob = await chatService.downloadAttachment(
                                  message.id,
                                  message.content.file?.name || ""
                                );
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = message.content.file?.name || "";
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                if (a.parentNode) {
                                  a.parentNode.removeChild(a);
                                }
                              }}
                              className={styles.downloadBtn}
                            >
                              <span>⬇️</span> Download
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className={styles.messageBody}>
                        {message.content.body}
                      </div>
                    )}

                    <div className={isCurrentUser ? styles.timestampSelf : styles.timestampOther}>
                      {formatChatTimestamp(message.created_at)}
                    </div>
                  </div>
                  {!isCurrentUser && onReply && (
                    <button
                      onClick={() => onReply(message)}
                      className={styles.replyBtn}
                      title="Reply"
                    >
                      <ArrowUturnLeftIcon className={styles.replyIcon} />
                    </button>
                  )}
                </>
              </div>
            );
          })}
      <div ref={messagesEndRef} />
    </div>
  );
};
